import { Injectable, Logger } from '@nestjs/common';
import { PrismaService } from '@cart/api-data-access';

/**
 * Rewrites media URLs that were persisted outside the media registry.
 *
 * `ProductImage.url` is the obvious one, and the one everybody remembers. It
 * is not the only one: review photos, the email sender logo, theme and
 * template preview images, and — worst — URLs buried inside JSON blobs
 * (`ThemeSetting.settings`, `StoreSetting.value`, `StorePage` content) all
 * hold copies. A migration that repoints the registry but misses a JSON blob
 * produces a broken image that nothing reports and nobody notices until a
 * merchant complains, so the sweep covers every one of them and reports what
 * it touched.
 *
 * JSON blobs are rewritten by string substitution over the serialized value.
 * That is deliberately blunt: the alternative is walking arbitrary
 * merchant-authored structures looking for anything URL-shaped, which fails
 * differently on every schema. Substituting a full, unique URL string cannot
 * match anything that is not that URL.
 */
@Injectable()
export class StorageUrlSweepService {
  private readonly logger = new Logger(StorageUrlSweepService.name);

  constructor(private readonly prisma: PrismaService) {}

  /** Rewrites one exact URL everywhere it may have been copied. */
  async rewriteUrl(organizationId: string, oldUrl: string, newUrl: string): Promise<number> {
    if (!oldUrl || oldUrl === newUrl) return 0;
    let touched = 0;

    const storeIds = await this.storeIdsFor(organizationId);
    if (!storeIds.length) return 0;

    const productImages = await this.prisma.productImage.updateMany({
      where: { storeId: { in: storeIds }, url: oldUrl },
      data: { url: newUrl },
    });
    touched += productImages.count;

    const reviewPhotos = await this.prisma.reviewPhoto.updateMany({
      where: { storeId: { in: storeIds }, url: oldUrl },
      data: { url: newUrl },
    });
    touched += reviewPhotos.count;

    const emailSettings = await this.prisma.emailSettings.updateMany({
      where: { storeId: { in: storeIds }, logoUrl: oldUrl },
      data: { logoUrl: newUrl },
    });
    touched += emailSettings.count;

    touched += await this.rewriteJsonBlobs(storeIds, oldUrl, newUrl);
    return touched;
  }

  private async rewriteJsonBlobs(storeIds: string[], oldUrl: string, newUrl: string): Promise<number> {
    let touched = 0;

    const themeSettings = await this.prisma.themeSetting.findMany({
      where: { storeId: { in: storeIds } },
      select: { id: true, settings: true },
    });
    for (const row of themeSettings) {
      const serialized = JSON.stringify(row.settings);
      if (!serialized.includes(oldUrl)) continue;
      await this.prisma.themeSetting.update({
        where: { id: row.id },
        data: { settings: JSON.parse(replaceAll(serialized, oldUrl, newUrl)) },
      });
      touched += 1;
    }

    const storeSettings = await this.prisma.storeSetting.findMany({
      where: { storeId: { in: storeIds } },
      select: { id: true, value: true },
    });
    for (const row of storeSettings) {
      const serialized = JSON.stringify(row.value);
      if (!serialized.includes(oldUrl)) continue;
      await this.prisma.storeSetting.update({
        where: { id: row.id },
        data: { value: JSON.parse(replaceAll(serialized, oldUrl, newUrl)) },
      });
      touched += 1;
    }

    return touched;
  }

  /**
   * Pre-flight audit for the local-disk retirement: counts every stored
   * reference to the old uploads path *before* the copy job runs, so the
   * migration starts from an enumerated list rather than an assumption about
   * where URLs live.
   */
  async auditLegacyUrls(organizationId: string): Promise<Record<string, number>> {
    const storeIds = await this.storeIdsFor(organizationId);
    if (!storeIds.length) return {};

    const legacy = { contains: '/api/uploads/' };

    const [productImages, reviewPhotos, emailSettings, themeSettings, storeSettings, storePages] = await Promise.all([
      this.prisma.productImage.count({ where: { storeId: { in: storeIds }, url: legacy } }),
      this.prisma.reviewPhoto.count({ where: { storeId: { in: storeIds }, url: legacy } }),
      this.prisma.emailSettings.count({ where: { storeId: { in: storeIds }, logoUrl: legacy } }),
      this.prisma.themeSetting.count({ where: { storeId: { in: storeIds } } }),
      this.prisma.storeSetting.count({ where: { storeId: { in: storeIds } } }),
      this.prisma.storePage.count({ where: { storeId: { in: storeIds } } }),
    ]);

    return {
      productImages,
      reviewPhotos,
      emailSettings,
      // JSON blobs are reported as row counts to inspect, not as matches:
      // counting matches would mean deserializing every blob in the audit.
      themeSettingRows: themeSettings,
      storeSettingRows: storeSettings,
      storePageRows: storePages,
    };
  }

  private async storeIdsFor(organizationId: string): Promise<string[]> {
    const stores = await this.prisma.store.findMany({
      where: { organizationId },
      select: { id: true },
    });
    return stores.map(s => s.id);
  }
}

function replaceAll(haystack: string, needle: string, replacement: string): string {
  return haystack.split(needle).join(replacement);
}
